// @ts-check
import { h } from "./dom.js";
import { icon as iconElement } from "./icons.js";

/**
 * Button (DS-03). Variants: primary | secondary | ghost | destructive | link.
 * Sizes: sm | md | lg. Icon-only buttons require `label`, used as the accessible
 * name and tooltip. `busy` marks the button as working without changing its width.
 *
 * @param {{
 *   label?: string, text?: string, icon?: string, iconEnd?: string,
 *   variant?: "primary" | "secondary" | "ghost" | "destructive" | "link",
 *   size?: "sm" | "md" | "lg", type?: "button" | "submit", disabled?: boolean,
 *   busy?: boolean, onClick?: (event: MouseEvent) => void, className?: string,
 *   attrs?: Record<string, any>,
 * }} options
 */
export function button({
  label,
  text,
  icon,
  iconEnd,
  variant = "secondary",
  size = "md",
  type = "button",
  disabled = false,
  busy = false,
  onClick,
  className = "",
  attrs = {},
}) {
  if (!text && !label) throw new Error("Buttons need visible text or a label");
  const element = h(
    "button",
    {
      type,
      class: `ui-button${className ? ` ${className}` : ""}`,
      dataset: { variant, size, iconOnly: text ? undefined : "true" },
      aria: { label: text ? undefined : label, busy: busy || undefined },
      title: text ? undefined : label,
      disabled,
      onClick,
      ...attrs,
    },
    icon && iconElement(icon),
    text && h("span", { class: "ui-button__label", text }),
    iconEnd && iconElement(iconEnd),
  );
  return element;
}

/** Toggle the working state of a button created by `button()`. @param {HTMLButtonElement} element @param {boolean} busy */
export function setBusy(element, busy) {
  if (busy) element.setAttribute("aria-busy", "true");
  else element.removeAttribute("aria-busy");
  element.disabled = busy;
}
